import { useState } from 'react';
import { withNamespaces } from 'react-i18next';
import {
    Button,
    Card,
    CardBody,
} from 'reactstrap';

import { getCountryFlag } from '../../../../../constants';
import { CallCheckStatuses } from '../../constants';

const PhoneNumberDetails = ({
    phone,
    t,
}) => {
    const [copied, setCopied] = useState(false);
    const reputation = CallCheckStatuses[phone.reputation];

    const copyNumber = async () => {
        if (!phone.number || phone.number === '—') return;

        try {
            await navigator.clipboard.writeText(phone.number);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (copyError) {
            setCopied(false);
            return copyError;
        }
    };

    return (
        <Card>
            <CardBody>
                <h5 className="mb-3">{t('Phone number details')}</h5>

                <div className="call-detail-row">
                    <span>{t('Phone number')}</span>
                    <span className="fw-medium">
                        {phone.number}
                        <button
                            aria-label={t('Copy phone number')}
                            className="btn btn-link p-0 ms-2"
                            onClick={copyNumber}
                            type="button"
                        >
                            <i className={copied ? 'ri-check-line text-success' : 'ri-file-copy-line text-muted'} />
                        </button>
                    </span>
                </div>
                <div className="call-detail-row">
                    <span>{t('Type')}</span>
                    <span>{t(phone.type)}</span>
                </div>
                <div className="call-detail-row">
                    <span>{t('Source')}</span>
                    <span>{t(phone.source)}</span>
                </div>
                <div className="call-detail-row">
                    <span>{t('Country')}</span>
                    <span className="d-flex align-items-center gap-2">
                        <img
                            alt={phone.countryName}
                            className="country-flag"
                            src={getCountryFlag(phone.countryCode)}
                        />
                        {`${t(phone.countryName)} (${phone.dialCode})`}
                    </span>
                </div>
                <div className="call-detail-row">
                    <span>{t('Format')}</span>
                    <span className="text-success">{t(phone.format)}</span>
                </div>
                <div className="call-detail-row">
                    <span>{t('Reputation check')}</span>
                    <span className={reputation.className}>
                        <i className={`${reputation.icon} align-middle me-1`} />
                        {t(reputation.title)}
                    </span>
                </div>
            </CardBody>
        </Card>
    );
};

const VerificationChecklist = ({
    items,
    t,
}) => (
    <Card>
        <CardBody>
            <h5 className="mb-3">{t('Verification checklist')}</h5>

            {items.map((item) => {
                const status = CallCheckStatuses[item.status];

                return (
                    <div className="call-detail-row call-check-row" key={item.id}>
                        <span>
                            <i className={`${status.icon} ${status.className} align-middle me-2`} />
                            {t(item.label)}
                        </span>
                        <span className={status.className}>{t(status.title)}</span>
                    </div>
                );
            })}
        </CardBody>
    </Card>
);

const RecommendedNextSteps = ({
    steps,
    t,
}) => (
    <Card>
        <CardBody>
            <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                <h5 className="mb-0">{t('Recommended next steps')}</h5>
                <span className="text-primary font-size-12 text-nowrap">
                    <i className="ri-sparkling-line align-middle me-1" />
                    {t('AI suggested')}
                </span>
            </div>

            {steps.map((step) => (
                <div className="call-next-step" key={step.id}>
                    <span className="avatar-xs">
                        <span className="avatar-title rounded-circle bg-light text-primary">
                            <i className={step.icon} />
                        </span>
                    </span>
                    <div className="flex-grow-1">
                        <div className="fw-medium">{t(step.title)}</div>
                        <small className="text-muted">{step.description}</small>
                    </div>
                    <Button className="p-0 text-nowrap" color="link" size="sm" type="button">
                        {t(step.action)}
                    </Button>
                </div>
            ))}
        </CardBody>
    </Card>
);

const CallVerificationAside = ({
    checklist,
    nextSteps,
    phone,
    t,
}) => (
    <>
        <PhoneNumberDetails phone={phone} t={t} />
        <VerificationChecklist items={checklist} t={t} />
        <RecommendedNextSteps steps={nextSteps} t={t} />
    </>
);

export default withNamespaces()(CallVerificationAside);
